import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useParams } from 'react-router';
import type { VendorEventInput, VendorWorkOrderView, WorkOrderEvent } from '@syndiqia/maintenance/contracts';
import { Logo } from '../components/Logo';
import { Alert, Field, Loading } from '../components/ui';
import { useI18n } from '../i18n';

// The contractor's page: opened by the work order's secure link, without signing in. It talks to /api/vendor/<token>.

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, init);
  const body = res.headers.get('content-type')?.includes('json') ? await res.json() : null;
  if (!res.ok) throw Object.assign(new Error(body?.message ?? `HTTP ${res.status}`), { status: res.status });
  return body as T;
}

export function VendorPage() {
  const { t, locale, setLocale, date, money } = useI18n();
  const v = t.vendor;
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  const page = useQuery({
    queryKey: ['vendor', token],
    queryFn: () => call<{ syndicate: string; workOrder: VendorWorkOrderView }>(`/api/vendor/${token}`),
    retry: false,
  });
  const post = useMutation({
    mutationFn: (input: VendorEventInput) => call<WorkOrderEvent>(`/api/vendor/${token}/events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor', token] }),
  });
  const upload = useMutation({
    mutationFn: ({ file, note }: { file: File; note: string }) => {
      const form = new FormData();
      form.append('note', note);
      form.append('file', file);
      return call<WorkOrderEvent>(`/api/vendor/${token}/files`, { method: 'POST', body: form });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor', token] }),
  });
  const invoice = useMutation({
    mutationFn: (form: FormData) => call<{ id: string }>(`/api/vendor/${token}/invoice`, { method: 'POST', body: form }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vendor', token] }),
  });
  const [mode, setMode] = useState<'note' | 'date' | 'file' | 'done' | 'declined' | 'invoice' | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceTotal, setInvoiceTotal] = useState('');
  const [note, setNote] = useState('');
  const [day, setDay] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const close = () => (setMode(null), setNote(''), setDay(''), setFile(null), setInvoiceNumber(''), setInvoiceTotal(''));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (mode === 'invoice' && file) {
      const form = new FormData();
      if (invoiceNumber.trim()) form.append('invoiceNumber', invoiceNumber.trim());
      if (invoiceTotal.trim()) form.append('total', invoiceTotal.trim());
      form.append('file', file);
      invoice.mutate(form, { onSuccess: close });
    } else if (mode === 'file' && file) upload.mutate({ file, note }, { onSuccess: close });
    else if (mode === 'date' && day) post.mutate({ kind: 'date_proposed', proposedDate: day, note }, { onSuccess: close });
    else if (mode === 'note' || mode === 'done' || mode === 'declined') post.mutate({ kind: mode, note }, { onSuccess: close });
  };

  const w = page.data?.workOrder;
  const events = w?.events ?? [];
  const accepted = events.some((e) => e.kind === 'accepted');
  const p = t.purchasing;
  const completed = w?.status === 'completed';
  return (
    <div className="vendor-page">
      <header className="topbar">
        <Logo />
        <button type="button" className="link-button" onClick={() => setLocale(locale === 'fr' ? 'en' : 'fr')}>
          {t.language}
        </button>
      </header>
      <main className="content vendor-content">
        {page.isPending && <Loading label={t.loading} />}
        {page.isError && <Alert>{v.invalid}</Alert>}
        {w && page.data && (
          <>
            <p className="muted">{v.forSyndicate(page.data.syndicate)}</p>
            <h1>
              {w.purpose === 'quote' ? t.purchasing.quoteTitle : v.title(w.number)} — {w.title}
              {w.priority === 'urgent' && <span className="badge badge-urgent">{t.maintenance.urgent}</span>}
            </h1>
            {w.description && <p className="message-body panel">{w.description}</p>}
            <dl className="facts panel">
              <dt>{v.location}</dt>
              <dd>{[w.assetName, w.location].filter(Boolean).join(' — ') || '—'}</dd>
              <dt>{v.scheduled}</dt>
              <dd>{w.scheduledOn ? date(w.scheduledOn) : v.none}</dd>
            </dl>

            {w.purpose === 'quote' && <QuoteSection token={token} view={w} />}
            {completed && <Alert tone="info">{p.closedWork}</Alert>}
            {w.purpose === 'work' && w.invoiceAddress && (
              <p className="panel small">
                <strong>{p.billing}</strong> {p.invoiceByEmail(w.invoiceAddress, w.number)}
              </p>
            )}
            {(post.isSuccess || upload.isSuccess || invoice.isSuccess) && !mode && <Alert tone="info">{v.sent}</Alert>}
            {(post.isError || upload.isError || invoice.isError) && <Alert>{((post.error ?? upload.error ?? invoice.error) as Error).message}</Alert>}

            {w.purpose === 'work' && !mode && completed && (
              <div className="vendor-actions">
                <button type="button" className={w.invoiceAddress ? 'button' : 'button button-primary'} onClick={() => setMode('invoice')}>
                  {w.invoiceAddress ? p.uploadInvoice : p.sendInvoice}
                </button>
              </div>
            )}
            {w.purpose === 'work' && !mode && !completed && (
              <div className="vendor-actions">
                {!accepted && (
                  <button type="button" className="button button-primary" disabled={post.isPending} onClick={() => post.mutate({ kind: 'accepted', note: '' })}>
                    ✓ {v.accept}
                  </button>
                )}
                <button type="button" className="button" onClick={() => setMode('date')}>
                  {v.proposeDate}
                </button>
                <button type="button" className="button" onClick={() => setMode('file')}>
                  {v.attach}
                </button>
                <button type="button" className="button" onClick={() => setMode('note')}>
                  {v.addNote}
                </button>
                <button type="button" className="button" onClick={() => setMode('done')}>
                  {v.done}
                </button>
                <button type="button" className="button" onClick={() => setMode('invoice')}>
                  {w.invoiceAddress ? p.uploadInvoice : p.sendInvoice}
                </button>
                {!accepted && (
                  <button type="button" className="link-button" onClick={() => setMode('declined')}>
                    {v.decline}
                  </button>
                )}
              </div>
            )}

            {mode && (
              <form className="panel form" onSubmit={submit}>
                <h2>{mode === 'date' ? v.proposeDate : mode === 'file' ? v.attach : mode === 'done' ? v.done : mode === 'declined' ? v.decline : mode === 'invoice' ? p.sendInvoice : v.addNote}</h2>
                {mode === 'invoice' && (
                  <>
                    <p className="muted small">{p.invoiceHint}</p>
                    <Field label={p.sendInvoice} hint={v.fileHint}>
                      <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    </Field>
                    <div className="row">
                      <Field label={p.invoiceNumber}>
                        <input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
                      </Field>
                      <Field label={p.invoiceTotal}>
                        <input inputMode="decimal" value={invoiceTotal} onChange={(e) => setInvoiceTotal(e.target.value)} />
                      </Field>
                    </div>
                  </>
                )}
                {mode === 'done' && <p className="muted small">{v.doneHint}</p>}
                {mode === 'date' && (
                  <Field label={v.date}>
                    <input type="date" value={day} onChange={(e) => setDay(e.target.value)} required autoFocus />
                  </Field>
                )}
                {mode === 'file' && (
                  <Field label={v.attach} hint={v.fileHint}>
                    <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                  </Field>
                )}
                {mode !== 'invoice' && (
                  <Field label={v.note} hint={mode === 'note' ? undefined : t.common.optional}>
                    <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} required={mode === 'note'} />
                  </Field>
                )}
                <div className="form-actions">
                  <button type="submit" className="button button-primary" disabled={post.isPending || upload.isPending || invoice.isPending || ((mode === 'file' || mode === 'invoice') && !file) || (mode === 'date' && !day)}>
                    {v.send}
                  </button>
                  <button type="button" className="button" onClick={close}>
                    {t.common.cancel}
                  </button>
                </div>
              </form>
            )}

            {w.invoices.length > 0 && (
              <section className="panel">
                <h2>{p.yourInvoices}</h2>
                <ul className="plain-list">
                  {w.invoices.map((i) => (
                    <li key={i.id}>
                      {i.invoiceNumber || '—'} · {i.totalCents != null ? money(i.totalCents) : '—'} · <span className={`badge badge-invoice-${i.status}`}>{p.invoiceStatuses[i.status]}</span>
                      {i.decisionNote && <div className="small muted">{i.decisionNote}</div>}
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {events.length > 0 && (
              <section className="panel">
                <h2>{v.history}</h2>
                <EventList events={events} fileUrl={(id) => `/api/vendor/${token}/files/${id}`} />
              </section>
            )}
            <p className="muted small">{v.access}</p>
          </>
        )}
      </main>
    </div>
  );
}

/** A quote request: the contractor gives a price, timing and file, or declines; they can revise until the board decides. */
function QuoteSection({ token, view }: { token: string; view: VendorWorkOrderView }) {
  const { t, date, money } = useI18n();
  const p = t.purchasing;
  const queryClient = useQueryClient();
  const q = view.quote;
  const decided = q?.status === 'selected' || q?.status === 'not_selected';
  const [amount, setAmount] = useState(q?.amountCents ? String(q.amountCents / 100) : '');
  const [delay, setDelay] = useState(q?.delayDays != null ? String(q.delayDays) : '');
  const [validUntil, setValidUntil] = useState(q?.validUntil ?? '');
  const [note, setNote] = useState(q?.note ?? '');
  const [file, setFile] = useState<File | null>(null);
  const done = () => queryClient.invalidateQueries({ queryKey: ['vendor', token] });
  const send = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('amount', amount);
      if (delay) form.append('delayDays', delay);
      if (validUntil) form.append('validUntil', validUntil);
      form.append('note', note);
      if (file) {
        form.append('file', file);
        return call(`/api/vendor/${token}/quote`, { method: 'POST', body: form });
      }
      const cents = Math.round(Number(amount.replace(',', '.').replace(/[^\d.]/g, '')) * 100);
      return call(`/api/vendor/${token}/quote`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ amountCents: cents, delayDays: delay ? Number(delay) : null, validUntil: validUntil || null, note }) });
    },
    onSuccess: done,
  });
  const decline = useMutation({ mutationFn: () => call(`/api/vendor/${token}/quote/decline`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ note }) }), onSuccess: done });

  return (
    <section className="panel">
      <h2>{p.yourQuote}</h2>
      {q && q.status !== 'requested' && (
        <p>
          <span className={`badge badge-quote-${q.status}`}>{p.quoteStatuses[q.status]}</span> {q.amountCents ? money(q.amountCents) : ''}
          {q.delayDays != null ? ` · ${p.days(q.delayDays)}` : ''}
          {q.validUntil ? ` · ${p.validUntil} ${date(q.validUntil)}` : ''}
          {q.fileName ? ` · 📎 ${q.fileName}` : ''}
        </p>
      )}
      {send.isSuccess && <Alert tone="info">{p.quoteSent}</Alert>}
      {(send.isError || decline.isError) && <Alert>{((send.error ?? decline.error) as Error).message}</Alert>}
      {!decided && (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            send.mutate();
          }}
        >
          <p className="muted small">{p.quoteIntro}</p>
          <div className="row">
            <Field label={p.quoteAmount}>
              <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required={!file} />
            </Field>
            <Field label={p.quoteDelay} hint={t.common.optional}>
              <input inputMode="numeric" value={delay} onChange={(e) => setDelay(e.target.value.replace(/\D/g, ''))} />
            </Field>
            <Field label={p.quoteValid} hint={t.common.optional}>
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
            </Field>
          </div>
          <Field label={p.quoteFile} hint={t.vendor.fileHint}>
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Field>
          <Field label={t.vendor.note} hint={t.common.optional}>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
          <div className="form-actions">
            <button type="submit" className="button button-primary" disabled={send.isPending || (!amount && !file)}>
              {p.quoteSend}
            </button>
            {q?.status !== 'declined_by_vendor' && (
              <button type="button" className="link-button" disabled={decline.isPending} onClick={() => decline.mutate()}>
                {p.quoteDecline}
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}

/** Work order activity (vendor and board), shared with the board's work order page. */
export function EventList({ events, fileUrl, onOpenFile }: { events: WorkOrderEvent[]; fileUrl: (eventId: string) => string | null; onOpenFile?: (eventId: string) => void }) {
  const { t, date } = useI18n();
  const v = t.vendor;
  return (
    <ol className="wo-events">
      {events.map((e) => {
        const url = e.kind === 'file' ? fileUrl(e.id) : null;
        return (
          <li key={e.id} className={`wo-event wo-event-${e.source}`}>
            <span className="muted small">
              {date(e.createdAt.slice(0, 10))} · {v.sources[e.source]}
            </span>
            <strong>
              {v.events[e.kind]}
              {e.proposedDate ? ` : ${date(e.proposedDate)}` : ''}
            </strong>
            {e.fileName &&
              (url ? (
                <a href={url} target="_blank" rel="noreferrer">
                  📎 {e.fileName}
                </a>
              ) : onOpenFile ? (
                <button type="button" className="link-button" onClick={() => onOpenFile(e.id)}>
                  📎 {e.fileName}
                </button>
              ) : (
                <span>📎 {e.fileName}</span>
              ))}
            {e.note && <p className="small message-body">{e.note}</p>}
          </li>
        );
      })}
    </ol>
  );
}
